import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_ORIGIN } from '../lib/api';

export interface CurrentUser {
  id: string;
  email: string | null;
  name: string;
  pictureUrl: string | null;
  countryCode: string | null;
  primaryLocale: string | null;
  hankoImageUrl: string | null;
  hankoSealText: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  /** True only for users whose email is on PLYEZ_ADMIN_EMAILS server-side.
   *  Workspace `owner`/`admin` roles are tenant-scoped and do NOT grant
   *  access to the operations console. */
  isPlyEZStaff: boolean;
  companyId: string | null;
  emailVerified: boolean;
  prefs?: {
    useReiwa: boolean;
    fullWidthDigits: boolean;
    showFurigana: boolean;
  };
}

/**
 * `auth/me` is mounted under the `/api` global prefix on the backend
 * (`auth/google` and `auth/google/callback` are the only auth routes
 * excluded). So this hook hits `/api/auth/me` directly.
 *
 * We use a raw axios call (not the shared `api` singleton) because that
 * singleton has a 401 redirect interceptor that's appropriate for app
 * pages but wrong for "am I logged in" probes — when the answer is "no",
 * we want to return null, not redirect to landing.
 */
export function useCurrentUser() {
  return useQuery<CurrentUser | null>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      try {
        const r = await axios.get<CurrentUser>(`${API_ORIGIN}/api/auth/me`, { withCredentials: true });
        return r.data;
      } catch (err: any) {
        if (err?.response?.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}
